import React from 'react'
import { Tooltip } from '@mui/material'
import { useTranslation } from 'react-i18next'
import Status from '../Status'
import { 
  formatBandwidth, 
  getTechnologyLabel, 
  getTechnologyDescription, 
  getFunctionLabel, 
  getFunctionDescription
} from '../utils/helpers'
import { WireLine } from '../use-search'

interface BandwidthStatusProps {
  saturation: number | null | undefined;
  useCase: string | null | undefined;
}

const BandwidthStatus: React.FC<BandwidthStatusProps> = ({ saturation, useCase }) => {
  const { t } = useTranslation()
  
  if (useCase !== 'wan_access') {
    return <>-</>
  }

  const isSaturated = (saturation ?? 0) > 30 * 60
  const statusText = isSaturated ? t('wan.table.bandwidth_saturated') : t('wan.table.bandwidth_optimal')
  const tooltipText = isSaturated 
    ? t('wan.table.bandwidth_saturated_tooltip')
    : t('wan.table.bandwidth_optimal_tooltip')
  
  return (
    <div>
      <Status 
        status={statusText}
        bgcolor={isSaturated ? '#FEE2E2' : '#E6F0D7'} 
        color={isSaturated ? '#DC2626' : '#587F31'} 
        tooltip={tooltipText}
      />
    </div>
  )
}

interface TechnologyWithTooltipProps {
  wireType: string | null | undefined;
}

const TechnologyWithTooltip: React.FC<TechnologyWithTooltipProps> = ({ wireType }) => {
  const label = getTechnologyLabel(wireType)
  const description = getTechnologyDescription(wireType)
  
  if (!description) {
    return <>{label}</>
  }
  
  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {label}
      <Tooltip title={description} placement="bottom" arrow>
        <i className="fa-regular fa-circle-info" style={{ marginLeft: '5px', fontSize: '12px', color: '#7A8096' }} />
      </Tooltip>
    </div>
  )
}

interface FunctionWithTooltipProps {
  useCase: string | null | undefined;
  ownerIsWifirst: boolean | null | undefined;
  wanAccessThroughIpsec: boolean | null | undefined;
}

const FunctionWithTooltip: React.FC<FunctionWithTooltipProps> = ({ useCase, ownerIsWifirst, wanAccessThroughIpsec }) => {
  const label = getFunctionLabel(useCase, ownerIsWifirst, wanAccessThroughIpsec)
  const description = getFunctionDescription(useCase, ownerIsWifirst, wanAccessThroughIpsec)
  
  if (!description) {
    return <>{label}</>
  }
  
  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {label}
      <Tooltip title={description} placement="bottom" arrow>
        <i className="fa-regular fa-circle-info" style={{ marginLeft: '5px', fontSize: '12px', color: '#7A8096' }} />
      </Tooltip>
    </div>
  )
}

interface ColumnConfig {
  name: string;
  header: {
    label: (ctx: { t: (key: string) => string }) => string;
  };
  value: (wireLine: WireLine, ctx?: { t: (key: string) => string }) => React.ReactNode;
  sortBy?: string;
  sortable?: boolean;
  hideOnCardOpen?: boolean;
  requiresRole?: string;
  internalOnly?: boolean;
}

const COLUMNS_CONFIG: ColumnConfig[] = [
  {
    name: 'sites',
    header: {
      label: ctx => ctx.t('wan.table.sites_col')
    },
    value: wireLine => (
      <div style={{ fontWeight: 500 }}>
        {wireLine.wifirstsite?.name || '-'}
      </div>
    ),
    sortBy: 'wifirstsite.name.raw',
    sortable: true
  },
  {
    name: 'provider',
    header: {
      label: ctx => ctx.t('wan.table.provider_col')
    },
    value: wireLine => wireLine.owner_is_wifirst ? 'Wifirst' : 'Client',
    sortBy: 'owner_is_wifirst',
    sortable: true
  },
  {
    name: 'technology',
    header: {
      label: ctx => ctx.t('wan.table.technology_col')
    },
    value: wireLine => <TechnologyWithTooltip wireType={wireLine.wire_type} />,
    sortBy: 'wire_type',
    sortable: true
  },
  {
    name: 'function',
    header: {
      label: ctx => ctx.t('wan.table.function_col')
    },
    value: wireLine => (
      <FunctionWithTooltip 
        useCase={wireLine.use_case} 
        ownerIsWifirst={wireLine.owner_is_wifirst} 
        wanAccessThroughIpsec={wireLine.wan_access_through_ipsec} 
      />
    ),
    sortBy: 'use_case',
    sortable: true
  },
  {
    name: 'bandwidth',
    header: {
      label: ctx => ctx.t('wan.table.bandwidth_col')
    },
    value: wireLine => <BandwidthStatus saturation={wireLine.saturation} useCase={wireLine.use_case} />,
    sortBy: 'saturation',
    sortable: true,
    requiresRole: 'wan_expert'
  },
  {
    name: 'download',
    header: {
      label: ctx => ctx.t('wan.table.download_col')
    },
    value: wireLine => formatBandwidth(wireLine.download, false, true, wireLine.wire_type),
    sortBy: 'download',
    sortable: true,
    hideOnCardOpen: true
  },
  {
    name: 'upload',
    header: {
      label: ctx => ctx.t('wan.table.upload_col')
    },
    value: wireLine => formatBandwidth(wireLine.upload, false, true, wireLine.wire_type),
    sortBy: 'upload',
    sortable: true,
    hideOnCardOpen: true
  }
]

export default COLUMNS_CONFIG
